import React, { useEffect, useRef, useState } from 'react';
import { brandsData } from '../data/mock';
import { Award } from 'lucide-react';

const BrandsSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    const currentSection = sectionRef.current;
    
    if (currentSection) {
      observer.observe(currentSection);
    }

    return () => {
      if (currentSection) {
        observer.unobserve(currentSection);
      }
    };
  }, []);

  return (
    <section ref={sectionRef} id="brands" className="py-24 bg-gradient-to-b from-gray-50 to-white">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className={`text-center mb-16 transform transition-all duration-700 ${
            isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
          }`}>
            <div className="inline-block bg-green-100 text-green-500 px-4 py-2 rounded-full font-semibold mb-4">
              Our Brand Portfolio
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              {brandsData.title}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {brandsData.description}
            </p>
          </div>

          {/* Brands Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {brandsData.brands.map((brand, index) => (
              <div
                key={index}
                className={`group bg-white rounded-xl p-6 shadow-md hover:shadow-2xl transition-all duration-500 border-2 border-gray-100 hover:border-green-500 transform ${
                  isVisible 
                    ? 'translate-y-0 opacity-100' 
                    : 'translate-y-10 opacity-0'
                }`}
                style={{ transitionDelay: `${index * 50 + 200}ms` }}
              >
                {/* Brand Header with Logo */}
                <div className="mb-4">
                  {/* Logo or Brand Name */}
                  {brand.logo ? (
                    <div className="mb-3">
                      <div className="h-16 flex items-center justify-start mb-2">
                        <img 
                          src={brand.logo} 
                          alt={brand.name}
                          className="max-h-12 max-w-[180px] object-contain"
                          style={{ filter: 'brightness(0.95)' }}
                        />
                      </div>
                      <h3 className="text-xl font-bold text-gray-900">
                        {brand.name}
                      </h3>
                    </div>
                  ) : (
                    <div className="mb-4 h-16 flex items-center justify-start">
                      <h3 className="text-2xl font-bold text-gray-900">
                        {brand.name}
                      </h3>
                    </div>
                  )}
                  
                  {/* Tagline */}
                  {brand.tagline && (
                    <p className="text-xs font-semibold text-green-600 uppercase tracking-wide">
                      {brand.tagline}
                    </p>
                  )}
                </div>

                {/* Specialization */}
                <p className="text-gray-600 leading-relaxed mb-4">
                  {brand.specialization}
                </p>

                {/* Categories */}
                <div className="flex flex-wrap gap-2">
                  {brand.categories.map((category, catIndex) => (
                    <span 
                      key={catIndex}
                      className="px-3 py-1 bg-gray-100 text-gray-700 text-xs font-medium rounded-full"
                    >
                      {category}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Bottom CTA */}
          <div className={`mt-16 text-center transform transition-all duration-700 delay-500 ${
            isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
          }`}>
            <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-2xl p-8 shadow-2xl">
              <p className="text-2xl md:text-3xl font-bold text-white mb-4">
                From A to Z, We've Got Every Part You Need
              </p>
              <p className="text-green-100 text-lg">
                Premium brands. Genuine parts. One reliable partner.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BrandsSection;
